/**
 * Phase 14 Plan 14-02 — ApproverPendingListPage
 *
 * Route: /logsheets/approver/pending
 * Permission gate: logsheet.approver.review
 *
 * Paginated queue of Checked logsheet entries for the approver's site.
 * Supports multi-select for bulk approval (T-14-23: capped at 100).
 * Mirrors CheckerPendingListPage style (Phase 13 Plan 13-02).
 */

import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { ApproverBulkApproveDialog } from '@/components/approver/ApproverBulkApproveDialog';
import { MonthLockedBadge } from '@/components/monthly/MonthLockedBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useApproverPendingQuery } from '@/hooks/useLogsheetApproverQueries';
import {
  useActiveCheckersForFilterQuery,
  useActiveMachinesForFilterQuery,
  useActiveSitesForFilterQuery,
} from '@/hooks/useReferenceDataQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatMachineName } from '@/lib/utils/machine-label';
import type { ApproverPendingItem, ApproverPendingListQuery } from '@/types/logsheet-approver';
import type { LogsheetStatus } from '@/types/logsheets-maker';

/**
 * Phase 26 — status filter options for the approver queue page. Approvers can
 * see entries in their site at any of these states (Submitted excluded — that's
 * the checker's queue, not the approver's view).
 */
type ApproverQueueStatus = 'Checked' | 'Approved' | 'Rejected';

function isApproverQueueStatus(s: string): s is ApproverQueueStatus {
  return s === 'Checked' || s === 'Approved' || s === 'Rejected';
}

const PAGE_SIZE = 20;

function parsePositiveInt(s: string | null): number | undefined {
  if (s === null) return undefined;
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function ApproverPendingListPage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFromDate = searchParams.get('fromDate') ?? '';
  const initialToDate = searchParams.get('toDate') ?? '';
  const initialSiteId = parsePositiveInt(searchParams.get('siteId'));
  const initialMachineId = parsePositiveInt(searchParams.get('machineId'));
  const initialCheckerId = parsePositiveInt(searchParams.get('checkerId'));
  const initialStatus: ApproverQueueStatus = (() => {
    const s = searchParams.get('status');
    return s !== null && isApproverQueueStatus(s) ? s : 'Checked';
  })();
  const initialPage = (() => {
    const p = Number.parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();

  const [fromDate, setFromDate] = useState<string>(initialFromDate);
  const [toDate, setToDate] = useState<string>(initialToDate);
  const [siteId, setSiteId] = useState<number | undefined>(initialSiteId);
  const [machineId, setMachineId] = useState<number | undefined>(initialMachineId);
  const [checkerId, setCheckerId] = useState<number | undefined>(initialCheckerId);
  const [status, setStatus] = useState<ApproverQueueStatus>(initialStatus);
  const [page, setPage] = useState<number>(initialPage);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState<boolean>(false);

  // Bulk-approve only makes sense on the default Checked queue.
  const isPendingView = status === 'Checked';

  const sitesQuery = useActiveSitesForFilterQuery();
  const machinesQuery = useActiveMachinesForFilterQuery();
  const checkersQuery = useActiveCheckersForFilterQuery();

  useEffect(() => {
    const next = new URLSearchParams();
    if (fromDate !== '') next.set('fromDate', fromDate);
    if (toDate !== '') next.set('toDate', toDate);
    if (siteId !== undefined) next.set('siteId', String(siteId));
    if (machineId !== undefined) next.set('machineId', String(machineId));
    if (checkerId !== undefined) next.set('checkerId', String(checkerId));
    if (status !== 'Checked') next.set('status', status);
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [fromDate, toDate, siteId, machineId, checkerId, status, page, setSearchParams]);

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [fromDate, toDate, siteId, machineId, checkerId, status]);

  const listQueryArgs: ApproverPendingListQuery = useMemo(() => {
    const out: { -readonly [K in keyof ApproverPendingListQuery]: ApproverPendingListQuery[K] } = {
      page,
      pageSize: PAGE_SIZE,
    };
    if (fromDate !== '') out.fromDate = fromDate;
    if (toDate !== '') out.toDate = toDate;
    if (siteId !== undefined) out.siteId = siteId;
    if (machineId !== undefined) out.machineId = machineId;
    if (checkerId !== undefined) out.checkerId = checkerId;
    if (status !== 'Checked') out.status = status as LogsheetStatus;
    return out;
  }, [fromDate, toDate, siteId, machineId, checkerId, status, page]);

  const listQuery = useApproverPendingQuery(listQueryArgs);

  const columns = useMemo<ColumnDef<ApproverPendingItem>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getRowModel().rows.length > 0 &&
              table.getRowModel().rows.every((r) => selectedIds.has(r.original.id))
            }
            onCheckedChange={(checked) => {
              const rows = table.getRowModel().rows;
              if (checked) {
                setSelectedIds((prev) => {
                  const next = new Set(prev);
                  rows.forEach((r) => next.add(r.original.id));
                  return next;
                });
              } else {
                setSelectedIds((prev) => {
                  const next = new Set(prev);
                  rows.forEach((r) => next.delete(r.original.id));
                  return next;
                });
              }
            }}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedIds.has(row.original.id)}
            onCheckedChange={(checked) => {
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (checked) {
                  next.add(row.original.id);
                } else {
                  next.delete(row.original.id);
                }
                return next;
              });
            }}
            aria-label={`Select entry ${String(row.original.id)}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        ),
        size: 40,
      },
      {
        accessorKey: 'entryDate',
        header: 'Date',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.entryDate}</span>,
      },
      {
        accessorKey: 'siteName',
        header: 'Site',
        cell: ({ row }) => <span>{row.original.siteName}</span>,
      },
      {
        accessorKey: 'machineName',
        header: 'Machine',
        cell: ({ row }) => (
          <span>
            {formatMachineName(
              row.original.machineName,
              row.original.vehicleNumber,
              row.original.machineCode,
            )}
          </span>
        ),
      },
      {
        accessorKey: 'makerName',
        header: 'Maker',
        cell: ({ row }) => <span>{row.original.makerName}</span>,
      },
      {
        accessorKey: 'checkerName',
        header: 'Checker',
        cell: ({ row }) => <span>{row.original.checkerName ?? '—'}</span>,
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <Badge variant={row.original.type === 'Idle' ? 'secondary' : 'default'}>
            {row.original.type}
          </Badge>
        ),
      },
      {
        accessorKey: 'kpisCount',
        header: 'KPIs',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.kpisCount}</span>,
      },
      {
        accessorKey: 'checkedAt',
        header: 'Checked At',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.checkedAt !== null
              ? new Date(row.original.checkedAt).toLocaleDateString()
              : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'daysPending',
        header: 'Days Pending',
        cell: ({ row }) => (
          <Badge variant={row.original.daysPending > 2 ? 'destructive' : 'secondary'}>
            {row.original.daysPending}d
          </Badge>
        ),
      },
      // Phase 15 Plan 15-02: Month lock status badge.
      {
        id: 'monthLocked',
        header: 'Month',
        cell: ({ row }) =>
          row.original.monthLocked === true ? (
            <span data-testid={`approver-pending-row-${String(row.original.id)}-month-locked-badge`}>
              <MonthLockedBadge locked={true} />
            </span>
          ) : null,
      },
    ],
    [selectedIds],
  );

  const data = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const table = useReactTable({
    data: data as ApproverPendingItem[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('logsheet.approver.review')) {
    return <Navigate to="/dashboard" replace />;
  }

  const selectedIdsArray = Array.from(selectedIds);

  return (
    <div className="space-y-6" data-testid="approver-pending-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Approver Queue</h1>
          <p className="text-muted-foreground">
            {status === 'Checked'
              ? 'Checked logsheet entries awaiting your approval.'
              : status === 'Approved'
                ? 'Approved logsheet entries in your site (read-only).'
                : 'Rejected logsheet entries in your site (read-only).'}
          </p>
        </div>
        {isPendingView && selectedIdsArray.length > 0 && (
          <Button
            type="button"
            onClick={() => {
              setBulkDialogOpen(true);
            }}
            data-testid="approver-pending-bulk-approve-trigger"
          >
            Bulk Approve ({selectedIdsArray.length})
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pending entries</CardTitle>
          <CardDescription>
            Filter by site, machine, checker or date range. Select entries for bulk approval.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44 space-y-1">
              <Label htmlFor="approver-filter-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => {
                  if (isApproverQueueStatus(v)) setStatus(v);
                }}
              >
                <SelectTrigger
                  id="approver-filter-status"
                  data-testid="approver-pending-filter-status"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Checked">Pending (Checked)</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="approver-filter-from-date">From date</Label>
              <Input
                id="approver-filter-from-date"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                }}
                data-testid="approver-pending-filter-from-date"
              />
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="approver-filter-to-date">To date</Label>
              <Input
                id="approver-filter-to-date"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                }}
                data-testid="approver-pending-filter-to-date"
              />
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="approver-filter-site">Site</Label>
              <Select
                value={siteId !== undefined ? String(siteId) : 'all'}
                onValueChange={(v) => {
                  if (v === 'all') {
                    setSiteId(undefined);
                  } else {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) setSiteId(n);
                  }
                }}
                disabled={sitesQuery.isLoading}
              >
                <SelectTrigger id="approver-filter-site" data-testid="approver-pending-filter-site">
                  <SelectValue placeholder={sitesQuery.isLoading ? 'Loading…' : 'All sites'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sites</SelectItem>
                  {sitesQuery.data?.items.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-44 space-y-1">
              <Label htmlFor="approver-filter-machine">Machine</Label>
              <Select
                value={machineId !== undefined ? String(machineId) : 'all'}
                onValueChange={(v) => {
                  if (v === 'all') {
                    setMachineId(undefined);
                  } else {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) setMachineId(n);
                  }
                }}
                disabled={machinesQuery.isLoading}
              >
                <SelectTrigger
                  id="approver-filter-machine"
                  data-testid="approver-pending-filter-machine"
                >
                  <SelectValue
                    placeholder={machinesQuery.isLoading ? 'Loading…' : 'All machines'}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All machines</SelectItem>
                  {machinesQuery.data?.items
                    .filter((m) => siteId === undefined || m.siteId === siteId)
                    .map((m) => (
                      <SelectItem key={m.id} value={String(m.id)}>
                        {m.name}{' '}
                        <span className="text-xs text-muted-foreground">({m.code})</span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-44 space-y-1">
              <Label htmlFor="approver-filter-checker">Checker</Label>
              <Select
                value={checkerId !== undefined ? String(checkerId) : 'all'}
                onValueChange={(v) => {
                  if (v === 'all') {
                    setCheckerId(undefined);
                  } else {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) setCheckerId(n);
                  }
                }}
                disabled={checkersQuery.isLoading}
              >
                <SelectTrigger
                  id="approver-filter-checker"
                  data-testid="approver-pending-filter-checker"
                >
                  <SelectValue
                    placeholder={checkersQuery.isLoading ? 'Loading…' : 'All checkers'}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All checkers</SelectItem>
                  {checkersQuery.data?.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {listQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="approver-pending-loading">
              Loading pending queue…
            </p>
          )}
          {listQuery.isError && (
            <p className="text-sm text-destructive" data-testid="approver-pending-error">
              Failed to load pending entries.
            </p>
          )}

          {listQuery.data && (
            <>
              <div data-testid="approver-pending-table">
                {/* Mobile card list */}
                <div className="md:hidden space-y-3">
                  {table.getRowModel().rows.length === 0 ? (
                    <p className="text-sm text-center py-8 text-muted-foreground">
                      No pending entries.
                    </p>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => {
                          void navigate(`/logsheets/view/${String(row.original.id)}`);
                        }}
                        className="w-full text-left rounded-lg border p-4 shadow-sm hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <div className="font-medium text-sm">{formatMachineName(row.original.machineName, row.original.vehicleNumber)}</div>
                            <div className="text-xs text-muted-foreground font-mono">{row.original.machineCode}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <Badge variant={row.original.type === 'Idle' ? 'secondary' : 'default'}>{row.original.type}</Badge>
                            <Badge variant={row.original.daysPending > 2 ? 'destructive' : 'secondary'}>{row.original.daysPending}d</Badge>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <div><span className="font-medium text-foreground">Date: </span><span className="font-mono">{row.original.entryDate}</span></div>
                          <div><span className="font-medium text-foreground">Site: </span>{row.original.siteName}</div>
                          <div><span className="font-medium text-foreground">Maker: </span>{row.original.makerName}</div>
                          <div><span className="font-medium text-foreground">Checker: </span>{row.original.checkerName ?? '—'}</div>
                          <div><span className="font-medium text-foreground">KPIs: </span><span className="font-mono">{row.original.kpisCount}</span></div>
                          {row.original.monthLocked === true && (
                            <div className="col-span-2">
                              <MonthLockedBadge locked={true} />
                            </div>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                          {hg.headers.map((h) => (
                            <TableHead key={h.id}>
                              {h.isPlaceholder
                                ? null
                                : flexRender(h.column.columnDef.header, h.getContext())}
                            </TableHead>
                          ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody>
                      {table.getRowModel().rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columns.length} className="text-center py-8">
                            <span data-testid="approver-pending-empty">No pending entries.</span>
                          </TableCell>
                        </TableRow>
                      ) : (
                        table.getRowModel().rows.map((row) => (
                          <TableRow
                            key={row.id}
                            data-testid={`approver-pending-row-${String(row.original.id)}`}
                            onClick={() => {
                              void navigate(`/logsheets/view/${String(row.original.id)}`);
                            }}
                            className="cursor-pointer hover:bg-muted/40"
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground">
                  Page {page} of {pageCount} · {total} total
                  {selectedIdsArray.length > 0 && (
                    <span className="ml-2 font-medium text-primary">
                      · {selectedIdsArray.length} selected
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={page <= 1}
                    data-testid="approver-pending-pagination-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.min(pageCount, p + 1));
                    }}
                    disabled={page >= pageCount}
                    data-testid="approver-pending-pagination-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ApproverBulkApproveDialog
        open={bulkDialogOpen}
        entryIds={selectedIdsArray}
        onClose={() => {
          setBulkDialogOpen(false);
        }}
        onSuccess={() => {
          setBulkDialogOpen(false);
          setSelectedIds(new Set());
        }}
      />
    </div>
  );
}
